import type { IDomain } from "../MarketBuilder.types";

/**
 * Checks that a given domain is valid
 * valid domain has this structure: <optionalSequence>.<sequence>.<sequence>
 */
const domainValidationRegex = /([a-я0-9|-]+\.)*[a-я0-9|-]+\.[a-я]+/;

/**
 * Cleans a given domain from prefix and suffix. removes "http(s)://" prefix, "www." prefix
 * and "/" suffix. this is used to clean domains after pasting them from clipboard.
 * example: https://www.someSite.com/ will be formatted to "someSite.com"
 */
const pastedDomainFormatRegex = /((http|https|ftp):\/\/)?(www\.)?([^/]+)(\/)?/;

/**
 * Fetches the pasted content from the clipboard, extracts any domains from it
 * and formats them as a list.
 */
export const parseDomainsFromClipboard = (clipboardEvent: ClipboardEvent): IDomain[] => {
    const pastedContent = clipboardEvent.clipboardData.getData("text").trim();
    if (!pastedContent || pastedContent.length <= 0) {return [];}

    const clipboardDomains = pastedContent
        .replace(/[\r\n]/g, ",")
        .split(",")
        .map(formatDomainText)
        .filter((domain) => Boolean(domain))
        .map((domain) => ({
            domain,
            favicon: "",
        }));

    return clipboardDomains;
};

export const formatDomainText = (domain: string) => {
    // Fetch the fourth capture group of this regex,
    // that is the cleaned domain.
    const cleanedDomain = pastedDomainFormatRegex?.exec(domain)?.[4];
    return cleanedDomain?.toLowerCase()?.trim() ?? "";
};

export const getValidDomains = (entities: IDomain[]) => {
    return entities.filter((entity) => {
        const hasValidStructure = domainValidationRegex.test(entity.domain);
        return hasValidStructure;
    });
};

export const getUniqueDomains = (domainsToAdd: IDomain[], existingDomains: IDomain[]) => {
    const entityIDs = new Set(existingDomains.map(({ domain }) => domain));
    const uniqueDomains = domainsToAdd.filter((entity) => {
        const { domain } = entity;
        if (entityIDs.has(domain)) {return false;}
        entityIDs.add(domain);
        return true;
    });
    return uniqueDomains;
};
